import { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import ReactSelect from 'react-select'

import { KTIcon } from '../../../../../_metronic/helpers'
import { SelectStyles } from '../../../../../components/Select'
import { useThemeMode } from '../../../../../_metronic/partials'
import { EmployeeProfile, ProfileMerchant } from '../../core/_models'
import { getMerchantsForSelect, IMerchantOption, attachMerchant, detachMerchant } from '../../api'

const AppSwal = withReactContent(Swal)

interface MerchantAccessProps {
  employee: EmployeeProfile
  onChanged: (profile: EmployeeProfile) => void
}

const errorText = (err: any): string =>
  err?.response?.data?.errors?.general_error ||
  err?.response?.data?.errors?.merchant ||
  'Не удалось выполнить запрос'

export const MerchantAccess = ({ employee, onChanged }: MerchantAccessProps) => {
  const { mode: theme } = useThemeMode()
  const [showModal, setShowModal] = useState(false)
  const [allMerchants, setAllMerchants] = useState<IMerchantOption[]>([])
  const [loadingMerchants, setLoadingMerchants] = useState(false)
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const attachedIds = new Set(employee.merchants.map((m) => m.id))
  const available = allMerchants.filter((m) => !attachedIds.has(m.id))

  const fetchMerchants = async () => {
    setLoadingMerchants(true)
    try {
      const res = await getMerchantsForSelect()
      if (res.data?.success && res.data.merchants) {
        setAllMerchants(res.data.merchants)
      }
    } catch (err) {
      console.log('error catched: ', err)
    } finally {
      setLoadingMerchants(false)
    }
  }

  useEffect(() => {
    if (showModal && !allMerchants.length) {
      fetchMerchants()
    }
  }, [showModal]) // eslint-disable-line react-hooks/exhaustive-deps

  const openModal = () => {
    setSelectedId(null)
    setShowModal(true)
  }

  const onConfirmAttach = () => {
    if (selectedId === null) return
    const merchant = available.find((m) => m.id === selectedId)
    AppSwal.fire({
      title: `Добавить мерчанта "${merchant?.title ?? selectedId}" сотруднику "${employee.uuid}"?`,
      showCancelButton: true,
      cancelButtonText: 'Нет',
      confirmButtonText: 'Да, добавить',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const res = await attachMerchant(employee.id, selectedId)
          if (!res.data?.success || !res.data.data) {
            throw new Error('bad response')
          }
          return res.data.data
        } catch (err) {
          AppSwal.showValidationMessage(errorText(err))
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        setShowModal(false)
        onChanged(result.value as EmployeeProfile)
        AppSwal.fire({ title: 'Мерчант добавлен', timer: 1500, showConfirmButton: false })
      }
    })
  }

  const onRemove = (merchant: ProfileMerchant) => {
    AppSwal.fire({
      title: `Убрать мерчанта "${merchant.title}" у сотрудника "${employee.uuid}"?`,
      showCancelButton: true,
      cancelButtonText: 'Нет',
      confirmButtonText: 'Да, убрать',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const res = await detachMerchant(employee.id, merchant.id)
          if (!res.data?.success || !res.data.data) {
            throw new Error('bad response')
          }
          return res.data.data
        } catch (err) {
          AppSwal.showValidationMessage(errorText(err))
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        onChanged(result.value as EmployeeProfile)
        AppSwal.fire({ title: 'Мерчант убран', timer: 1500, showConfirmButton: false })
      }
    })
  }

  return (
    <div className='card mt-6'>
      <div className='card-header border-0 pt-5 d-flex justify-content-between align-items-center'>
        <h3 className='card-title fw-bold fs-3'>Доступные мерчанты</h3>
        <button className='btn btn-sm btn-success' onClick={openModal}>
          <KTIcon iconName='plus' className='fs-2' />
          Добавить мерчанта
        </button>
      </div>
      <div className='card-body py-3'>
        {employee.merchants.length === 0 ? (
          <div className='text-muted'>Нет привязанных мерчантов</div>
        ) : (
          <table className='table table-row-dashed align-middle gs-0 gy-3'>
            <thead>
              <tr className='fw-bold text-muted'>
                <th>ID</th>
                <th>Название</th>
                <th>Активен</th>
                <th className='text-end'>Действия</th>
              </tr>
            </thead>
            <tbody>
              {employee.merchants.map((m) => (
                <tr key={m.id}>
                  <td className='fw-bold'>{m.id}</td>
                  <td>{m.title}</td>
                  <td>{m.enabled ? 'Да' : 'Нет'}</td>
                  <td className='text-end'>
                    <div
                      className='cursor-pointer d-inline-block'
                      onClick={() => onRemove(m)}
                      title='Убрать'
                    >
                      <KTIcon className='fs-1' iconName='trash' />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        aria-hidden='true'
        dialogClassName='modal-dialog modal-dialog-centered mw-600px'
        show={showModal}
        backdrop={true}
      >
        <div className='modal-header'>
          <h2>Добавить мерчанта</h2>
          <div
            className='btn btn-sm btn-icon btn-active-color-primary'
            onClick={() => setShowModal(false)}
          >
            <KTIcon className='fs-1' iconName='cross' />
          </div>
        </div>
        <div className='modal-body py-lg-10 px-lg-10'>
          {loadingMerchants ? (
            <div className='form-control form-control-lg form-control-solid bg-light'>
              Загрузка мерчантов...
            </div>
          ) : (
            <ReactSelect
              options={available.map((m) => ({ value: m.id, label: m.title }))}
              value={
                available
                  .filter((m) => m.id === selectedId)
                  .map((m) => ({ value: m.id, label: m.title }))[0] ?? null
              }
              onChange={(opt: any) => setSelectedId(opt ? opt.value : null)}
              placeholder='Выберите мерчанта'
              isClearable
              styles={SelectStyles(theme)}
            />
          )}
        </div>
        <div className='card-footer d-flex justify-content-end py-6 px-9'>
          <button type='button' className='btn' onClick={() => setShowModal(false)}>
            Отмена
          </button>
          <button
            type='button'
            className='btn btn-primary'
            disabled={selectedId === null}
            onClick={onConfirmAttach}
          >
            Добавить
          </button>
        </div>
      </Modal>
    </div>
  )
}
